import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons'
import { EEAP_SLOTS, type EeapSlot } from '../../utils/eeapSlots'
import {
  hrToolsService,
  type EeapSlotMapping,
  type FormFieldCatalog,
} from '../../services/hrToolsService'

function targetToken(mapping?: EeapSlotMapping | null) {
  if (!mapping?.targetType || !mapping?.targetId) return ''
  return `${mapping.targetType}:${mapping.targetId}`
}

function parseTarget(token: string): EeapSlotMapping | null {
  const idx = token.indexOf(':')
  if (idx <= 0) return null
  const targetType = token.slice(0, idx)
  const targetId = token.slice(idx + 1).trim()
  if ((targetType !== 'field' && targetType !== 'group') || !targetId) return null
  return { targetType, targetId }
}

const ADMIN_FINANCIAL_SLOT_KEYS = new Set([
  'currentAllowance',
  'currentAdvanceBalance',
  'totalRequestCost',
  'newAdvanceBalance',
])

function slotHint(slot: EeapSlot) {
  if (ADMIN_FINANCIAL_SLOT_KEYS.has(slot.key)) {
    return 'Admin only — not filled from Profile. The employee estimate is used for screening only.'
  }
  if (slot.valueKind === 'radio') {
    return `Radio: ${(slot.options || []).map((o) => o.label).join(', ')}`
  }
  if (slot.valueKind === 'signature') return 'Signature field'
  if (slot.valueKind === 'signatureDate') return 'Signature date field'
  return 'Field or group'
}

const HrToolsTab: React.FC = () => {
  const [catalogs, setCatalogs] = useState<FormFieldCatalog[]>([])
  const [formName, setFormName] = useState('')
  const [mappings, setMappings] = useState<Record<string, EeapSlotMapping>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const selected = useMemo(
    () => catalogs.find((c) => c.formName === formName) || null,
    [catalogs, formName],
  )

  const usedTokens = useMemo(() => {
    const used = new Set<string>()
    for (const mapped of Object.values(mappings)) {
      const token = targetToken(mapped)
      if (token) used.add(token)
    }
    return used
  }, [mappings])

  const unmappedCount = EEAP_SLOTS.filter((slot) => !targetToken(mappings[slot.key])).length

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const [list, saved] = await Promise.all([
          hrToolsService.listFormCatalog(),
          hrToolsService.getEeapFormMapping(),
        ])
        if (cancelled) return
        setCatalogs(list)
        setFormName(saved.formName || '')
        setMappings(saved.mappings || {})
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load HR tools')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const handleFormChange = (nextName: string) => {
    setFormName(nextName)
    setMessage(null)
    const nextCatalog = catalogs.find((c) => c.formName === nextName)
    if (!nextCatalog) {
      setMappings({})
      return
    }
    const allowed = new Set([
      ...nextCatalog.fields.map((f) => `field:${f.id}`),
      ...nextCatalog.groups.map((g) => `group:${g.id}`),
    ])
    setMappings((prev) => {
      const next: Record<string, EeapSlotMapping> = {}
      for (const [key, mapped] of Object.entries(prev)) {
        const token = targetToken(mapped)
        if (token && allowed.has(token)) next[key] = mapped
      }
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const saved = await hrToolsService.saveEeapFormMapping(formName, mappings)
      setMappings(saved.mappings || {})
      setMessage('EEAP field map saved. Profile EEAP uses this form for employee requests.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save mapping')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">
          <FontAwesomeIcon icon={faScrewdriverWrench} />
          EEAP form mapping
        </h2>
        <p className="text-sm text-base-content/70">
          Select the EEAP form, then map every Profile EEAP field to a field or group on that form.
          Radio values such as request type should usually map to a radio group. Unused mapped fields can stay empty at fill time.
        </p>

        {loading ? (
          <div className="py-8 text-base-content/70">Loading forms…</div>
        ) : (
          <>
            <div className="form-control w-full max-w-xl mt-2">
              <label className="label"><span className="label-text font-medium">Form</span></label>
              <select
                className="select select-bordered w-full"
                value={formName}
                onChange={(e) => handleFormChange(e.target.value)}
              >
                <option value="">Select a form…</option>
                {catalogs.map((c) => (
                  <option key={c.formName} value={c.formName}>{c.formName}</option>
                ))}
              </select>
            </div>

            {error && <div className="alert alert-error mt-3"><span>{error}</span></div>}
            {message && <div className="alert alert-success mt-3"><span>{message}</span></div>}

            {!formName || !selected ? (
              <div className="py-6 text-base-content/70">Choose a form to see its fields.</div>
            ) : (
              <>
                <div className="overflow-x-auto mt-4">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>EEAP field</th>
                        <th>Expected</th>
                        <th>Form field or group</th>
                      </tr>
                    </thead>
                    <tbody>
                      {EEAP_SLOTS.map((slot) => {
                        const current = targetToken(mappings[slot.key])
                        return (
                          <tr key={slot.key} className={!current ? 'bg-warning/10' : ''}>
                            <td className="font-medium whitespace-nowrap">{slot.label}</td>
                            <td className="text-xs text-base-content/60 min-w-48">{slotHint(slot)}</td>
                            <td>
                              <select
                                className="select select-bordered select-sm w-full min-w-64"
                                value={current}
                                onChange={(e) => {
                                  const parsed = parseTarget(e.target.value)
                                  setMappings((prev) => {
                                    const next = { ...prev }
                                    if (!parsed) delete next[slot.key]
                                    else next[slot.key] = parsed
                                    return next
                                  })
                                }}
                              >
                                <option value="">Select…</option>
                                {selected.groups.length > 0 && (
                                  <optgroup label="Groups">
                                    {selected.groups.map((g) => {
                                      const token = `group:${g.id}`
                                      const taken = usedTokens.has(token) && token !== current
                                      return (
                                        <option key={token} value={token} disabled={taken}>
                                          {g.name} {g.mode === 'radio' ? '(radio)' : '(group)'}
                                        </option>
                                      )
                                    })}
                                  </optgroup>
                                )}
                                {selected.fields.length > 0 && (
                                  <optgroup label="Fields">
                                    {selected.fields.map((f) => {
                                      const token = `field:${f.id}`
                                      const taken = usedTokens.has(token) && token !== current
                                      return (
                                        <option key={token} value={token} disabled={taken}>
                                          {f.label} ({f.type})
                                        </option>
                                      )
                                    })}
                                  </optgroup>
                                )}
                              </select>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-base-content/60">
                    {unmappedCount === 0 ? 'All EEAP fields are mapped.' : `${unmappedCount} field${unmappedCount === 1 ? '' : 's'} still need a target.`}
                  </span>
                  <button
                    className="btn btn-primary"
                    onClick={() => void handleSave()}
                    disabled={saving || !formName || unmappedCount > 0}
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {saving ? 'Saving…' : 'Save mapping'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default HrToolsTab
